import React, {Fragment} from 'react';
import styled from 'styled-components';
import {Link, withRouter} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';

import * as flags from '../../lib/flags';
import * as actions from '../../state/actions';
import {useUser, useSignedIn} from '../../lib/user';
import {default as SidebarLink} from './Link';
import Logo from '../Logo';
import Icon from '../Icon';
import Avatar from '../Avatar';
import Menu from '../Menu';
import Blur from '../Blur';
import {T} from '../I18n';
import HasManagementRole from "../../security/rules/HasManagementRole";
import HasAdminRole from "../../security/rules/HasAdminRole";
import {canViewInventory} from "../../screens/InventoryScreen/access";

function Sidebar({activeLink, history, ...props}) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const user = useUser();
  const signedIn = useSignedIn();
  const dispatch = useDispatch();
  const notificationCount = useSelector(state => state.notificationCount);

  if (!signedIn) {
    return null;
  }

  function toggleMenu() {
    setMenuOpen(open => !open);
  }

  async function handleLogout() {
    await dispatch(actions.reset());
    history.push('/');
  }

  return (
    <Container {...props}>
      <Top>
        <LogoLink to="/" menuOpen={menuOpen}>
          <Logo white size={48} src={props.logoSrc} />
        </LogoLink>
        <AvatarContainer>
          <Avatar id="sidebar-avatar" user={user} size="2em" onClick={toggleMenu}/>
        </AvatarContainer>
      </Top>
      <Blur blurred={menuOpen} radius="5px">
        <SidebarLink
          active={activeLink === 'Dashboard'}
          to="/"
          icon={<Icon name="home" />}
          label="Home"
          count={notificationCount > 0 ? notificationCount : null}
          color={props.sidebarTextColor}
        />
        <SidebarLink
            to={import.meta.env.VITE_CLIPBOARD_URL}
            icon={<Icon name="app-clipboard" />}
            label="Clipboard"
            color={props.sidebarTextColor}
            external
        />
        {(user.role !== "USER") && <SidebarLink
          active={activeLink === 'Reports'}
          to="/reports"
          icon={<Icon name="pie-chart" />}
          label="Reports"
          color={props.sidebarTextColor}
        />}
        <SidebarLink
          active={activeLink === 'Forms'}
          to="/forms"
          icon={<Icon name="clipboard" />}
          label="Forms"
          color={props.sidebarTextColor}
        />
        <SidebarLink 
          active={activeLink === 'generateCodes'}
          to="/codes"
          icon={<Icon name='grid' />}
          label={"QR Codes"}
          color={props.sidebarTextColor}
        />
        {(flags.isOn(user, 'TRANSFORMATION_ENABLED') && <SidebarLink 
          active={activeLink === 'Transforms'}
          to="/transforms"
          icon={<Icon name="copy" />}
          label="Transforms"
          color={props.sidebarTextColor}
        />)}
        {HasManagementRole(user) && (
          <Fragment>
            {canViewInventory(user) && (
              <SidebarLink
                active={activeLink === 'Inventory'}
                to="/inventory"
                icon={<Icon name="package" />}
                label="Inventory"
                color={props.sidebarTextColor}
              />
            )}
            <SidebarLink
              active={activeLink === 'Catalog'}
              to="/catalog"
              icon={<Icon name="database" />}
              label="Catalog"
              color={props.sidebarTextColor}
            />
            <SidebarLink
              active={activeLink === 'Reminders'}
              to="/reminders"
              icon={<Icon name="bell" />}
              label="Reminders"
              color={props.sidebarTextColor}
            />
            <SidebarLink
              active={activeLink === 'Users'}
              to="/users"
              icon={<Icon name="user" />}
              label="Users"
              color={props.sidebarTextColor}
            />
          </Fragment>
        )}
        {HasAdminRole(user) && (
          <Fragment>
            {flags.isOn(user, 'SCALE_PRINT_ENABLED') && (
              <SidebarLink
                active={activeLink === 'Devices'}
                to="/devices"
                icon={<Icon name="printer" />}
                label="Devices"
                color={props.sidebarTextColor}
              />
            )}
            <SidebarLink
              active={activeLink === 'admin'}
              to="/admin"
              icon={<Icon name="settings" />}
              label="Admin"
              color={props.sidebarTextColor}
            />
          </Fragment>
        )}
      </Blur>
      <Menu
        open={menuOpen}
        position="bottom-end"
        relativeTo="sidebar-avatar"
        width="13rem"
        offsetY={8}
        onClose={toggleMenu}>
        <T element={Username} noTranslate title={user.userName}>{user.userName}</T>
        <Menu.Divider />
        <Menu.Option to="https://forms.gle/WrsT6CQfddCg4wEg9" target="_blank">Report an issue</Menu.Option>
        <Menu.Option to="https://forms.gle/EE3Ah1GBQF8zvMwY8" target="_blank">Give us feedback</Menu.Option>
        <Menu.Divider />
        <Menu.Option onClick={handleLogout}>Logout</Menu.Option>
      </Menu>
    </Container>
  );
}

const Container = styled.div`
  background: ${props => props.sidebarBackgroundColor || "linear-gradient(#0049ee, #107aff)"};
  width: 15rem;
  height: 100%;
  box-shadow: 0 0.125rem 0 rgba(90, 97, 105, 0.11),
              0 0.25rem 0.5rem rgba(90, 97, 105, 0.12),
              0 0.625rem 0.625rem rgba(90, 97, 105, 0.06),
              0 0.4375rem 4.375rem rgba(165, 182, 201, 0.1);
`;

const Top = styled.div`
  display: flex;
  flex-direction: row;
`;

const LogoLink = styled(({menuOpen, ...props}) => <Link {...props} />)`
  padding: 1rem;

  ${props => {
    if (props.menuOpen) {
      return `filter: blur(5px);`;
    }
  }}
`;

const AvatarContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 1rem;
`;

const Username = styled(Menu.Text)`
  font-weight: bold;
  text-overflow: ellipsis;
  white-space: nowrap; 
  overflow: hidden;
`;

export default withRouter(Sidebar);